import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DrawerComponent from "../../components/DrawerComponent/DrawerComponent";
import AppBarComponent from "../../components/AppBarComponent/AppBarComponent";
import CalculoBurdenPanel from "../../components/Proyectos/CalculoBurdenPanel";
import BurdenMedidoresAdminModal from "../../components/Proyectos/BurdenMedidoresAdminModal";
import { isProyectosAdminRole } from "../../utils/proyectosRol";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

type Proyecto = {
  id: number;
  codigo: string;
  nombre: string;
  nivel_tension?: string | null;
};

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

export default function CalculoBurdenPage() {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [proyectos, setProyectos] = React.useState<Proyecto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [seleccionado, setSeleccionado] = React.useState<Proyecto | null>(null);
  const [distanciaRed, setDistanciaRed] = React.useState<string | number | null>(null);
  const [modalMedidoresOpen, setModalMedidoresOpen] = React.useState(false);
  const [medidoresVersion, setMedidoresVersion] = React.useState(0);
  const esAdmin = isProyectosAdminRole();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleDrawerOpen = () => setOpen((prev) => !prev);
  const handleDrawerClose = () => setOpen(false);

  React.useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${getProyectosApiBaseUrl()}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "No fue posible listar proyectos.");
        setProyectos(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err.message || "Error al cargar proyectos.");
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  React.useEffect(() => {
    if (!seleccionado?.id) {
      setDistanciaRed(null);
      return;
    }
    const cargarSolicitud = async () => {
      try {
        const response = await fetch(`${getProyectosApiBaseUrl()}/${seleccionado.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await response.json();
        if (!response.ok) return;
        const etapa = (data?.etapas || []).find((e: any) => e?.etapa_key === "solicitud_servicio");
        let form: Record<string, unknown> = {};
        try {
          const parsed = etapa?.observaciones ? JSON.parse(etapa.observaciones) : {};
          form =
            parsed?.formulario && typeof parsed.formulario === "object"
              ? parsed.formulario
              : parsed;
        } catch {
          form = {};
        }
        setDistanciaRed((form as any)?.distancia_red ?? null);
      } catch {
        setDistanciaRed(null);
      }
    };
    cargarSolicitud();
  }, [seleccionado?.id]);

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />
      <AppBarComponent
        open={open}
        handleDrawerOpen={handleDrawerOpen}
        auth
        anchorEl={anchorEl}
        handleMenu={handleMenu}
        handleClose={handleClose}
        title="Cálculo de burden"
      />
      <DrawerComponent open={open} handleDrawerClose={handleDrawerClose} />
      <Box component="main" sx={{ flexGrow: 1, overflow: "auto", pt: "116px", pb: "56px" }}>
        <Container maxWidth="lg" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "flex-start" }}
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                      Cálculo de burden (TC / TT)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verificación según plantilla EMCALI: medidores homologados, carga de conductor y criterio 25
                      %–100 % del burden nominal.
                    </Typography>
                  </Box>
                  {esAdmin && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setModalMedidoresOpen(true)}
                      sx={{ flexShrink: 0 }}
                    >
                      Editar medidores homologados
                    </Button>
                  )}
                </Stack>

                <BurdenMedidoresAdminModal
                  open={modalMedidoresOpen}
                  onClose={() => setModalMedidoresOpen(false)}
                  onActualizado={() => setMedidoresVersion((v) => v + 1)}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Autocomplete
                      options={proyectos}
                      getOptionLabel={(p) => `${p.codigo} — ${p.nombre}`}
                      value={seleccionado}
                      onChange={(_, v) => setSeleccionado(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Proyecto" size="small" sx={{ mb: 2 }} />
                      )}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                    />
                    <CalculoBurdenPanel
                      key={medidoresVersion}
                      mostrarBotonMedidoresAdmin={false}
                      proyectoId={seleccionado?.id ?? null}
                      proyectoCodigo={seleccionado?.codigo}
                      proyectoNombre={seleccionado?.nombre}
                      nivelTension={seleccionado?.nivel_tension ?? undefined}
                      distanciaRedM={distanciaRed}
                    />
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
